import React, { useCallback, useState } from "react";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useTextOverflow from "@hooks/useTextOverflow";

/**
 * 자식 요소의 텍스트가 실제로 잘렸을 때만 툴팁을 노출한다.
 *
 * Typography 가 아닌 요소(Link, Grid, Button 등)에 CSS 로 말줄임이 걸린 경우에 사용한다.
 * 자식을 그대로 렌더링하고 ref 만 주입하므로 DOM·스타일 변화가 없다. (DAM-306)
 *
 * @param {React.ReactNode} title 툴팁에 표시할 전체 텍스트. 생략하면 렌더된 자식의 텍스트를 사용한다.
 * @param {React.ReactElement} children ref 를 받을 수 있는 단일 요소
 */
const OverflowTooltip = ({ title, placement = "bottom", children, ...rest }) => {
    const [element, setElement] = useState(null);
    const isOverflow = useTextOverflow(element, [title]);
    // 자식이 임의의 JSX 인 경우(컨테이너에 말줄임이 걸린 경우) 실제 렌더된 텍스트를 툴팁으로 쓴다.
    const resolvedTitle = title ?? element?.textContent ?? "";

    // 자식이 이미 ref 를 갖고 있으면 함께 유지한다. (자식이 forwardRef 가 아니면 측정이 동작하지 않고 툴팁만 뜨지 않는다)
    const childRef = React.isValidElement(children) ? children.ref : null;
    const handleRef = useCallback(
        (node) => {
            setElement(node);
            if (typeof childRef === "function") childRef(node);
            else if (childRef) childRef.current = node;
        },
        [childRef],
    );

    if (!React.isValidElement(children)) return children ?? null;

    return (
        <Tooltip title={isOverflow ? resolvedTitle : ""} placement={placement} {...rest}>
            {React.cloneElement(children, { ref: handleRef })}
        </Tooltip>
    );
};

export default OverflowTooltip;
